import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import { BaseModal } from 'components/UI';
import { TextField } from 'components/FormFields';
import { FETCH_STATE } from 'constants/common';
import { AccessDeviceActions } from 'state/modules/accessDevice';
import { DeviceExternalTypeSelect } from 'components/DataFields/DeviceExternalTypeSelect';
import { useBuildSchemaByBroker, isRenderField } from './FormFieldsFactory';

const CreateNFCCardModal = ({ id, broker, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const [brokerSlug, setBrokerSlug] = useState('');

  const { accessDevice, fetchingAccessDevice, creatingAccessDevice, updatingAccessDevice, errors } =
    useSelector((state) => state.accessDevice);

  const NFCCardSchema = Yup.object().shape(useBuildSchemaByBroker(brokerSlug));
  const [nameFieldError, setNameFieldError] = useState(null);
  const [deviceIdFieldError, setDeviceIdFieldError] = useState(null);

  useEffect(() => {
    if (errors && errors.details) {
      if (errors.details.find((error) => error.field === 'name')) {
        setNameFieldError(i18n.t('nfc.name_already_exists'));
      } else {
        setNameFieldError(null);
      }
      if (errors.details.find((error) => error.field === 'device_id')) {
        setDeviceIdFieldError(i18n.t('nfc.device_id_already_exists'));
      } else {
        setDeviceIdFieldError(null);
      }
    } else {
      setNameFieldError(null);
      setDeviceIdFieldError(null);
    }
  }, [errors]);

  useEffect(() => {
    if (id && accessDevice?.id !== id && open) {
      dispatch(AccessDeviceActions.fetchAccessDeviceById(id));
    }
  }, [id, open, dispatch, accessDevice?.id]);

  useEffect(() => {
    if (broker) {
      const brokerSlug = broker?.broker_slug || broker || '';
      setBrokerSlug(brokerSlug);
    }
    if (accessDevice && accessDevice.brokers?.length > 0) {
      const brokerSlug = accessDevice.brokers[0].broker_slug;
      setBrokerSlug(brokerSlug);
    }
  }, [broker, accessDevice]);

  const onSubmit = (values) => {
    if (id) {
      dispatch(AccessDeviceActions.updateAccessDevice(id, values));
    } else {
      dispatch(AccessDeviceActions.createAccessDevice({ ...values, broker: brokerSlug }));
    }
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  const loading =
    fetchingAccessDevice === FETCH_STATE.LOADING ||
    creatingAccessDevice === FETCH_STATE.LOADING ||
    updatingAccessDevice === FETCH_STATE.LOADING;

  const initialValues = {
    ad_type: 'CARD',
    name: accessDevice?.name || '',
    external_type: accessDevice?.external_type || '',
    device_id: accessDevice?.device_id || '',
  };

  return (
    <BaseModal
      size="sm"
      open={open}
      maxWidth={550}
      hasEnterButton
      onEnter={handleSubmit}
      enableCloseButton={true}
      handleClick={handleClick}
      title={i18n.t(id ? 'nfc.edit_access_device' : 'nfc.add_access_device')}
      enterButtonLabel={id ? i18n.t('common.update') : i18n.t('common.create')}
      loading={loading}
      data-cy="nfc-modal-create"
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={NFCCardSchema}
      >
        {({ handleSubmit }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <p>{i18n.t('nfc.create_description')}</p>
            {isRenderField(brokerSlug, 'name') && (
              <Box mt={1}>
                <Field
                  name="name"
                  label={i18n.t('nfc.card_name')}
                  component={TextField}
                  asyncErrors={errors.name || nameFieldError}
                  data-cy="nfc-field-name"
                  required={false}
                />
              </Box>
            )}
            {isRenderField(brokerSlug, 'external_type') && (
              <Box mt={1}>
                <Field
                  name="external_type"
                  label={i18n.t('nfc.external_type')}
                  component={DeviceExternalTypeSelect}
                  asyncErrors={errors.external_type}
                  data-cy="nfc-field-external_type"
                  disabled={id}
                />
              </Box>
            )}
            {isRenderField(brokerSlug, 'device_id') && (
              <Box mt={1}>
                <Field
                  name="device_id"
                  label={i18n.t('nfc.device_id')}
                  component={TextField}
                  asyncErrors={errors.device_id || deviceIdFieldError}
                  data-cy="nfc-field-device-id"
                  disabled={id}
                />
              </Box>
            )}
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
};

CreateNFCCardModal.propTypes = {
  id: PropTypes.number,
  open: PropTypes.bool,
  broker: PropTypes.string,
  handleClick: PropTypes.func,
};

export default CreateNFCCardModal;
